/**
 * Public "Services" marketplace catalog — Profile → Services → website `/services`.
 *
 * The filter ids and labels are the same ones the promoter Service Provider
 * application uses ({@link PROMOTER_SERVICE_TYPE_OPTIONS} / {@link PROMOTER_EVENT_TYPE_OPTIONS} /
 * {@link PROMOTER_MUSIC_STYLE_OPTIONS}),
 * so what a provider selects in their profile is exactly what visitors browse by.
 */
import {
  Briefcase,
  Building2,
  CakeSlice,
  Coffee,
  Disc3,
  Handshake,
  HeartHandshake,
  Martini,
  Music4,
  PartyPopper,
  Shirt,
  Sparkles,
  Sun,
  Tent,
  Trophy,
  UtensilsCrossed,
  Waves,
  Wine,
  type LucideIcon,
} from "lucide-react";
import {
  PROMOTER_EVENT_TYPE_OPTIONS,
  PROMOTER_MUSIC_STYLE_OPTIONS,
  PROMOTER_SERVICE_TYPE_OPTIONS,
} from "./promoterServiceApplication";
import type { ServiceSortValue } from "@/types/api";

export const SERVICE_EVENT_TYPE_OPTIONS = PROMOTER_EVENT_TYPE_OPTIONS;
export const SERVICE_MUSIC_STYLE_OPTIONS = PROMOTER_MUSIC_STYLE_OPTIONS;

/** Provider types visitors filter by ("Select performer types" grid on `/services`). */
export const SERVICE_PROVIDER_TYPE_OPTIONS = PROMOTER_SERVICE_TYPE_OPTIONS;

const EVENT_TYPE_LABELS = new Map(PROMOTER_EVENT_TYPE_OPTIONS.map((o) => [o.id, o.label]));
const MUSIC_STYLE_LABELS = new Map(PROMOTER_MUSIC_STYLE_OPTIONS.map((o) => [o.id, o.label]));

/** Label → id, so answers stored as labels ("Nightclub") still resolve to catalog ids. */
const EVENT_TYPE_IDS_BY_LABEL = new Map(
  PROMOTER_EVENT_TYPE_OPTIONS.map((o) => [o.label.toLowerCase(), o.id])
);
/** Label → canonical label, so casing/spacing variants ("hip-hop / rap") render nicely. */
const EVENT_TYPE_LABELS_BY_LABEL = new Map(
  PROMOTER_EVENT_TYPE_OPTIONS.map((o) => [o.label.toLowerCase(), o.label])
);
const MUSIC_STYLE_LABELS_BY_LABEL = new Map(
  PROMOTER_MUSIC_STYLE_OPTIONS.map((o) => [o.label.toLowerCase(), o.label])
);

/** Looks like a catalog id (`private-party`) rather than a free-text answer. */
const CATALOG_ID_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * Answers reach the API in two shapes: catalog ids ("nightclub") and the labels the
 * Profile → Services chips store ("Nightclub"). This resolves both to a display label —
 * canonical for catalog values, a readable title-cased form for legacy ids, and the raw
 * value untouched for custom entries members typed themselves.
 */
function resolveAnswerLabel(
  value: string,
  byLabel: Map<string, string>,
  fallbackLabel?: string
): string {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return value;

  const canonical = byLabel.get(trimmed.toLowerCase());
  if (canonical) return canonical;
  if (fallbackLabel) return fallbackLabel;

  if (CATALOG_ID_RE.test(trimmed)) {
    return trimmed
      .split("-")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }

  return trimmed;
}

/** Display label for a stored event type — accepts a catalog id or a stored label. */
export function serviceEventTypeLabel(value: string): string {
  return resolveAnswerLabel(value, EVENT_TYPE_LABELS_BY_LABEL, EVENT_TYPE_LABELS.get(value));
}

/** Display label for a stored music style — accepts a catalog id or a stored label. */
export function serviceMusicStyleLabel(value: string): string {
  return resolveAnswerLabel(value, MUSIC_STYLE_LABELS_BY_LABEL, MUSIC_STYLE_LABELS.get(value));
}

/**
 * Catalog id behind a stored event type, whichever shape was saved — drives icons and
 * blurbs so a label-stored row ("Nightclub") still shows the club icon, not the fallback.
 */
export function serviceEventTypeId(value: string): string | undefined {
  if (EVENT_TYPE_LABELS.has(value)) return value;
  return EVENT_TYPE_IDS_BY_LABEL.get((value ?? "").trim().toLowerCase());
}

/** Icon + pitch used by the category rail on `/services`. */
export interface ServiceCategoryVisual {
  icon: LucideIcon;
  blurb: string;
}

/** Keyed by {@link PROMOTER_EVENT_TYPE_OPTIONS} id. */
export const SERVICE_CATEGORY_VISUALS: Record<string, ServiceCategoryVisual> = {
  nightclub: { icon: Disc3, blurb: "Table hosts, guest list, and club-night promotion" },
  "day-party": { icon: Sun, blurb: "Daytime parties, brunch clubs, and poolside crowds" },
  rooftop: { icon: Building2, blurb: "Rooftop and skyline event promotion" },
  "pool-party": { icon: Waves, blurb: "Pool and day-club takeovers" },
  concert: { icon: Music4, blurb: "Live shows, artist nights, and ticket sales" },
  festival: { icon: Tent, blurb: "Multi-day festivals and outdoor stages" },
  brunch: { icon: Coffee, blurb: "Bottomless brunch and daytime tables" },
  dinner: { icon: UtensilsCrossed, blurb: "Dinner parties and restaurant nights" },
  "private-party": { icon: PartyPopper, blurb: "Private events and members-only nights" },
  birthday: { icon: CakeSlice, blurb: "Birthday celebrations and group bookings" },
  corporate: { icon: Briefcase, blurb: "Corporate events, mixers, and after-works" },
  fashion: { icon: Shirt, blurb: "Fashion shows, launches, and brand nights" },
  "album-release": { icon: Sparkles, blurb: "Album drops, listening parties, and showcases" },
  sports: { icon: Trophy, blurb: "Watch parties and game-day events" },
  networking: { icon: Handshake, blurb: "Mixers, founder nights, and professional networking" },
  "after-work": { icon: Martini, blurb: "Happy hours and after-work drinks crowds" },
  "speed-dating": { icon: HeartHandshake, blurb: "Speed dating nights and singles mixers" },
  "wine-tasting": { icon: Wine, blurb: "Wine tastings, sommelier nights, and pairings" },
};

export const SERVICE_CATEGORY_DEFAULT_VISUAL: ServiceCategoryVisual = {
  icon: Sparkles,
  blurb: "Nightlife and event services",
};

/** Icon + pitch for an event type — accepts a catalog id or a stored label. */
export function serviceCategoryVisual(value: string): ServiceCategoryVisual {
  const id = serviceEventTypeId(value) ?? value;
  return SERVICE_CATEGORY_VISUALS[id] ?? SERVICE_CATEGORY_DEFAULT_VISUAL;
}

/** Provider-type display labels, keyed by catalog id (`event-planner` → "Event Planner"). */
const PROVIDER_TYPE_LABELS = new Map(PROMOTER_SERVICE_TYPE_OPTIONS.map((o) => [o.id, o.label]));
/** Label → id / canonical label / emoji, so answers stored as labels still resolve. */
const PROVIDER_TYPE_IDS_BY_LABEL = new Map(
  PROMOTER_SERVICE_TYPE_OPTIONS.map((o) => [o.label.toLowerCase(), o.id])
);
const PROVIDER_TYPE_LABELS_BY_LABEL = new Map(
  PROMOTER_SERVICE_TYPE_OPTIONS.map((o) => [o.label.toLowerCase(), o.label])
);
const PROVIDER_TYPE_EMOJI = new Map(
  PROMOTER_SERVICE_TYPE_OPTIONS.flatMap((o) => [
    [o.id, o.emoji ?? ""] as [string, string],
    [o.label.toLowerCase(), o.emoji ?? ""] as [string, string],
  ])
);

/** Display label for a stored service type — accepts a catalog id or a stored label. */
export function serviceProviderTypeLabel(value: string): string {
  return resolveAnswerLabel(value, PROVIDER_TYPE_LABELS_BY_LABEL, PROVIDER_TYPE_LABELS.get(value));
}

/** Catalog id behind a stored service type, so the filter matches either stored shape. */
export function serviceProviderTypeId(value: string): string | undefined {
  if (PROVIDER_TYPE_LABELS.has(value)) return value;
  return PROVIDER_TYPE_IDS_BY_LABEL.get((value ?? "").trim().toLowerCase());
}

/** Emoji badge for a stored service type; empty string when it isn't a catalog value. */
export function serviceProviderTypeEmoji(value: string): string {
  const trimmed = (value ?? "").trim();
  return PROVIDER_TYPE_EMOJI.get(trimmed) ?? PROVIDER_TYPE_EMOJI.get(trimmed.toLowerCase()) ?? "";
}

/** Sort options offered in the `/services` filter bar (mirrors the API's `sort` param). */
export const SERVICE_SORT_OPTIONS: { value: ServiceSortValue; label: string }[] = [
  { value: "featured", label: "Featured" },
  { value: "mostExperienced", label: "Most Experienced" },
  { value: "recentlyApproved", label: "Recently Approved" },
  { value: "alphabeticalOrder", label: "A - Z" },
];

/** Human-readable compact number for audience reach ("45K"). */
export function formatCompactCount(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0";
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}K`;
  return String(value);
}

/** "From 12% commission" — the marketplace's price line. */
export function formatCommission(percent: number | null): string {
  if (percent === null || !Number.isFinite(percent) || percent <= 0) return "Custom commission";
  const rounded = Number.isInteger(percent) ? String(percent) : percent.toFixed(1);
  return `${rounded}% commission`;
}

/**
 * The price line for a provider: an hourly rate for the types that quote one (every type except
 * Promoters), otherwise the commission on sales. Providers who quote neither show "Rate on
 * request", so cards never advertise a 0% / "custom" commission they never answered.
 */
export function formatProviderRate(hourlyRate: number | null, commissionPercent: number | null): string {
  if (hourlyRate !== null && Number.isFinite(hourlyRate) && hourlyRate > 0) {
    const rounded = Number.isInteger(hourlyRate) ? String(hourlyRate) : hourlyRate.toFixed(2);
    return `$${rounded}/hr`;
  }

  if (commissionPercent !== null && Number.isFinite(commissionPercent) && commissionPercent > 0) {
    return formatCommission(commissionPercent);
  }

  return "Rate on request";
}

/** "8 yrs experience" */
export function formatExperience(years: number | null): string | null {
  if (years === null || !Number.isFinite(years) || years <= 0) return null;
  return years === 1 ? "1 yr experience" : `${years} yrs experience`;
}
